import { batch, createMemo, createResource } from "solid-js";
import {
  type ChannelEntry,
  listChannels,
  listNetworks,
  type MeResponse,
  me,
  type Network,
  type RawNetwork,
  tagNetwork,
} from "./api";
import { token } from "./auth";
import { setBadge } from "./badge";
import { identityMoved } from "./identityMoved";
import { identityScopedStore } from "./identityScopedStore";
import { applyMeEnvelope } from "./readCursor";

// Network/channel tree resources. Module-singleton — every consumer
// reads the same fine-grained signals; no provider boilerplate.
//
// Resources:
//   * `networks` — `GET /networks` keyed on the bearer signal. Solid's
//     createResource handles the bearer-keyed re-fetch automatically
//     on token rotation.
//   * `user` — `GET /me`, gated on the bearer; the user_name builds
//     per-channel topic strings (`grappa:user:{name}/...`).
//   * `channelsBySlug` — fans out one `GET /networks/:slug/channels`
//     per network the user is on. Slug is the topic-vocabulary
//     identifier; the integer id is REST-internal only.
//
// Per-channel verbs (scrollback, selection, subscribe) live in their
// own modules — see the D3/A4 verb-keyed split. This module owns only
// the network/user resources; the schemas (`Network`, `ChannelEntry`,
// `MeResponse`) live in `api.ts`.
//
// `identityScopedStore` wraps the resources in a `createRoot` (anchors
// their internal memos to an owner) AND registers the token-rotation
// purge below — the same factory `scrollback.ts` / `selection.ts` use.
//
// #281 — identity-change purge. These resources re-fetch on rotation,
// but re-fetch is NOT the same as clear: Solid 1.9's createResource
// `load()` RETAINS the last resolved value whenever the source signal
// goes falsy (`if (lookup == null || lookup === false) loadEnd(pr,
// untrack(value))`). So on detach (`token: tokA → null`) `user` — and,
// keyed off it, `networks` + `channelsBySlug` — keep the PREVIOUS
// account's data. On re-login as a different account (`null → tokB`)
// TWO arms replay that STALE data under the new bearer: the
// token-tracking effects in `subscribe.ts` (channels / query /
// dm-listener / server loops, each gated on a `networks()` entry) AND
// the HomePane featured fetch (gated on `user()` via `home.ts`
// `homeData`). The result is a burst of `GET /networks/<prev-net>/…/
// messages` + `/featured`, all 404, which trips the host `http-404`
// fail2ban jail and firewall-bans the client IP. The `onIdentityChange`
// purge below clears all three so no previous-identity network / channel
// state survives the switch. (An earlier moduledoc claimed "no on(token)
// cleanup needed" — that was the #281 bug's origin.)
//
// BUG1-FIX: `mutateNetworks` is exported so `userTopic.ts` can patch
// a single network's `nick` field in-place when the server broadcasts
// `own_nick_changed` — no REST round-trip needed, and the reactive
// consumers (subscribe.ts DM-listener loop, query-window own-nick skip)
// see the updated nick immediately.

const exports = identityScopedStore((onIdentityChange) => {
  const [user, { mutate: mutateUserResource, refetch: refetchUserResource }] = createResource<
    MeResponse | null,
    string | null
  >(token, async (t, info) => {
    if (!t) return null;
    // #394 — an explicit `refetchUser()` is a read-after-write (HomePane's
    // connect-a-network, the BootErrorBoundary retry): it must not be served
    // an answer that left before the write. A source-driven load (boot, token
    // rotation) is unattended and may share one.
    const m = await me(t, info.refetching ? "fresh" : "shared");
    // #818 — refuse to hydrate for an identity that is no longer current.
    // `createResource` discards the stale VALUE (a rotation refetches and only
    // the latest promise feeds `user()`), but it cannot cancel an await: the
    // two SIDE-EFFECTS below run whenever this promise settles, even seconds
    // after the token rotated. Both write module-global caches keyed by
    // nothing but (slug, channel), so account A's `/me` landing after the
    // switch seeds B's cursors and B's badge with A's numbers. The cursor half
    // is the worse one: a channel B has never read gets a `read_cursor: null`
    // join reply, which `applyJoinReply` deliberately no-ops on, so A's value
    // is never corrected — B's backlog renders as already seen, and the
    // inverse (A's lower cursor on B's window) can drive a spurious MARKREAD.
    //
    // The identity-TRANSITION is already handled (readCursor's own `on(token)`
    // arm and the `onIdentityChange` purge below both fire on rotation); what
    // was missing is refusing a write that arrives AFTER the purge — the rule
    // `identityMoved` owns. Kept in the caller rather than inside
    // `applyMeEnvelope` because the check guards the whole block (`setBadge`
    // shares this await and this bug) and because `readCursor` cannot know
    // which identity produced an envelope handed to it.
    if (identityMoved(t)) return m;
    // CP29 R-4: hydrate the readCursor signal map from the bulk envelope
    // BEFORE downstream consumers (subscribe.ts join effects, etc.)
    // observe `user()` and start joining channel topics. The join-reply
    // arm (`applyJoinReply`) layers per-channel refreshes on top later;
    // this is the cold-load primer. Default to `{}` if the server omits
    // the field (older test mocks predating the field landing in
    // `MeResponse`) — production /me always emits it.
    applyMeEnvelope(m.read_cursors ?? {});
    // PWA icon badge door #2 (2026-06-21): seed the badge signal from the
    // `/me` notify-worthy unread total so the home-screen icon /
    // document.title reflect the count before any push or read_cursor_set
    // arrives. `badge.ts` has no networks/selection imports, so seeding
    // here (unlike `unread_counts`) closes no import cycle. Default 0 for
    // older test mocks / pre-field servers.
    setBadge(m.badge_count ?? 0);
    // Bucket C (2026-06-01) — the parallel `unread_counts` cold-load
    // primer for `selection.ts`'s `serverSeedCounts` lives inside
    // selection.ts itself (an `on(user)` effect there reads
    // `m.unread_counts` and calls `applySeedEnvelope`). Routing the
    // call back through networks.ts would close a networks ↔ selection
    // import cycle that breaks under vitest module re-entry: the named
    // binding `applySeedEnvelope` resolves to `undefined` because
    // selection.ts is still mid-eval when networks.ts captures it.
    // selection.ts already imports `user` from this module for its
    // existing selection-clear arm, so the new effect is one extra
    // line in a module that already lives downstream.
    return m;
  });

  // Networks resource is keyed on `user` (not raw token) so the
  // boundary tagger reads the explicit server-set `kind` discriminator
  // off each row (no-silent-drops B6.9a HIGH-24). Pre-fix the server
  // emitted two implicit shapes (visitor: bare; user: nick +
  // connection_state fields) and cic joined against `me().kind` to
  // tag — correct but added a silent dependency on /me being fetched
  // first, AND a discriminator drift between the server's me-render
  // and the network shape would mis-tag every row. Server now sets
  // `kind: "user" | "visitor"` so cic reads the discriminator AT the
  // source. tagNetwork drops rows that fail the user-subject contract
  // (missing nick or connection_state) — the missing-nick branch was
  // the cic H3 silent root cause; H4 closed it at the boundary so it
  // can't leak into the typed store.
  const [networks, { mutate: mutateNetworksResource, refetch: refetchNetworksResource }] =
    createResource<Network[], MeResponse | null>(user, async (currentMe) => {
      if (!currentMe) return [];
      const t = token();
      if (!t) return [];
      const raw: RawNetwork[] = await listNetworks(t);
      const tagged: Network[] = [];
      for (const r of raw) {
        const n = tagNetwork(r);
        if (n !== null) tagged.push(n);
      }
      return tagged;
    });

  const [channelsBySlug, { mutate: mutateChannelsResource, refetch: refetchChannelsResource }] =
    createResource<Record<string, ChannelEntry[]>, Network[]>(networks, async (nets) => {
      if (!nets || nets.length === 0) return {};
      const t = token();
      if (!t) return {};
      const entries = await Promise.all(
        nets.map(async (n) => [n.slug, await listChannels(t, n.slug)] as const),
      );
      return Object.fromEntries(entries);
    });

  // #281 — purge on identity change (see moduledoc for the createResource
  // stale-value mechanism). `user` is the ROOT of the burst: it's keyed
  // on `token`, `networks` (keyed on `user`) + `channelsBySlug` (keyed on
  // `networks`) cascade from it, and `home.ts` `homeData` (→ the HomePane
  // featured fetch) reads `user()` directly — so clearing `user` is what
  // stops BOTH replay arms (the subscribe.ts loops via empty networks /
  // channels, AND the featured fetch via empty homeData). We still purge
  // all three explicitly: `batch` makes it one atomic update (no
  // dependent computed observes a half-purged new-token / stale-networks
  // state that could re-fire a fetch), and no consumer (isAdmin, ownNick
  // / socketUserName, sidebar) ever reads the previous identity during
  // the refetch window. The factory's `prev != null && t !== prev` filter
  // fires only on the real transitions (logout tokA→null, rotation
  // tokA→tokB) — initial registration + cold login are masked.
  onIdentityChange(() => {
    batch(() => {
      mutateUserResource(null);
      mutateNetworksResource([]);
      mutateChannelsResource({});
    });
  });

  const refetchChannels = (): void => {
    void refetchChannelsResource();
  };

  // Codebase review 2026-05-08 cross-infra H1: pre-fix the
  // `connection_state_changed` event was emitted via raw
  // `Phoenix.PubSub.broadcast/3` and never reached cic over WS. cic
  // worked around by REST refetch on PATCH return — but other tabs /
  // clients on the same account didn't see the state flip until they
  // re-rendered. The fix routes the event through `broadcast_event/2`
  // on the user-level topic; this exposes a refetch hook so
  // `userTopic.ts` can pull the updated `Credential.connection_state`
  // / reason / changed_at fields without duplicating wire shape into
  // the client.
  //
  // issue 2059 — QUEUE A TRAILING REFETCH INSTEAD OF DROPPING IT.
  //
  // Measured, not assumed: with two calls issued back to back, the SECOND
  // GET never leaves. The bench answers the first call `parked` and the
  // second `failing`; the store ends on `parked` and the second stubbed
  // answer is still queued, unconsumed. `createResource.refetch()` does not
  // start a second fetch while one is in flight, so the caller's second
  // request is simply lost.
  //
  // That is not a cosmetic loss. `/reconnect` emits two
  // `connection_state_changed` events in quick succession and each triggers
  // a refetch: lose the unpark one and the store keeps the parked row —
  // the sidebar section stays greyed and the Home parked card stays up,
  // with nothing further scheduled to correct it. It is the second half of
  // what `issue1796-reconnect-bounces-network.spec.ts` asserts, and it is a
  // DIFFERENT defect from the lost home redirect, with a different cure.
  //
  // Trailing-coalesce rather than a full chain: any number of calls arriving
  // during a flight collapse into exactly ONE follow-up, which starts after
  // that flight ends. The invariant that matters is "a refetch requested
  // after the last state change is answered after it", and one trailing run
  // gives that without turning a burst of N events into N round-trips.
  //
  // THAT INVARIANT IS SCOPED TO THIS DOOR — it is NOT a property of
  // `networks()` (issue 2055, measured 2026-09-11 against the real modules).
  // The resource is keyed on `user`, so a `refetchUser()` resolving mid-flight
  // moves the SOURCE and starts a second load the queue never sees; that load
  // claims Solid's promise pointer and the in-flight answer is discarded, just
  // as it was before this cure. Measured on the literal adjacent pair in
  // `HomePane.tsx` (`refetchUser(); refetchNetworks();`): the store sequence
  // observed for the slug collapses to ["connected", "connected"] and the
  // park is gone.
  //
  // WHY THAT IS NOT AN OPEN DEFECT, and what would make it one. The SNAPSHOT
  // survives — the source-driven request leaves AFTER the discarded one, so
  // the winning answer is the fresher one; what is lost is a TRANSITION, and
  // that only bites a consumer deriving one from this sample. Since 2059 the
  // sole such consumer is selection.ts's bucket D on a WS gap, and that path
  // never reaches this door: `resumeResync` and `subscribe.ts`'s socket-edge
  // arm call `refetchNetworks` + `refetchChannels`, never `refetchUser`. The
  // routes that DO reach it (HomePane's attach; the identity / profile /
  // avatar / password doors in `lifecycle.ts`) all have the event feed
  // carrying the transition instead. It takes BOTH conditions at once and no
  // route holds them today — a new caller pairing `refetchUser()` with a
  // refetch whose payload carries a state change would be the first.
  let refetchInFlight = false;
  let refetchPending = false;
  const runNetworksRefetch = async (): Promise<void> => {
    refetchInFlight = true;
    try {
      await refetchNetworksResource();
    } finally {
      refetchInFlight = false;
      if (refetchPending) {
        refetchPending = false;
        void runNetworksRefetch();
      }
    }
  };
  const refetchNetworks = (): void => {
    if (refetchInFlight) {
      refetchPending = true;
      return;
    }
    void runNetworksRefetch();
  };

  // #126 — re-fetch GET /me after a visitor disconnect/reconnect so the
  // whereis-derived `connected` flag updates → the SettingsDrawer flips
  // its disconnect ⇄ reconnect toggle. Visitors have no
  // `connection_state_changed` broadcast (that's a user-topic event),
  // so the verb handler refetches explicitly rather than waiting on a
  // push.
  const refetchUser = (): void => {
    void refetchUserResource();
  };

  // Patch the nick for one network in the in-memory networks list.
  // Called by userTopic.ts when `own_nick_changed` arrives so the
  // DM-listener and query-window loops see the correct own-nick
  // without waiting for the next GET /networks refetch.
  //
  // #476 union-note — BOTH subjects carry a per-network `nick` (the visitor
  // row converged onto the user twin, ruling A; #211 phase 7 gave a visitor
  // real per-network identity that CAN change via the identity editor). The
  // retired `kind === "user"` gate no-op'd a visitor's `own_nick_changed`,
  // stranding the DM-listener's own-nick after a visitor per-network rename.
  // Patch by id regardless of kind — `nick` is present on either variant.
  const mutateNetworkNick = (networkId: number, nick: string): void => {
    mutateNetworksResource((prev) => {
      if (!prev) return prev;
      return prev.map((n) => (n.id === networkId ? { ...n, nick } : n));
    });
  };

  // bnd-A2: canonical slug→Network lookup. Pre-fix compose.ts repeated
  // `networks()?.find((n) => n.slug === slug)?.id` 14× across slash-
  // command handlers. The memo invalidates whenever `networks()`
  // updates (post-/connect, post-/disconnect, bearer rotation), so
  // callers see new entries without manual cache management.
  const networksBySlug = createMemo(() => {
    const list = networks();
    if (!list) return new Map<string, Network>();
    return new Map(list.map((n) => [n.slug, n]));
  });

  const networkBySlug = (slug: string): Network | undefined => networksBySlug().get(slug);

  const networkIdBySlug = (slug: string): number | undefined => networkBySlug(slug)?.id;

  // UX-4 bucket N — admin predicate hoisted here from Shell.tsx +
  // SettingsDrawer.tsx + Sidebar.tsx (three callsites, rule-of-three
  // threshold). Single source of truth for the M-cluster M-7 admin
  // gate (drawer entry, AdminPane mount, demote-mid-session
  // auto-close, sidebar admin row visibility). Narrows the
  // `MeResponse` discriminated union so the user-only `is_admin`
  // field is reachable; visitor + null both collapse to false.
  const isAdmin = (): boolean => {
    const u = user();
    return u?.kind === "user" && u.is_admin === true;
  };

  return {
    networks,
    user,
    channelsBySlug,
    refetchChannels,
    refetchNetworks,
    refetchUser,
    mutateNetworkNick,
    networkBySlug,
    networkIdBySlug,
    isAdmin,
  };
});

export const networks = exports.networks;
export const user = exports.user;
export const channelsBySlug = exports.channelsBySlug;
export const refetchChannels = exports.refetchChannels;
export const refetchNetworks = exports.refetchNetworks;
export const refetchUser = exports.refetchUser;
export const mutateNetworkNick = exports.mutateNetworkNick;
export const networkBySlug = exports.networkBySlug;
export const networkIdBySlug = exports.networkIdBySlug;
export const isAdmin = exports.isAdmin;
